/**
 * The tour's state and its commands (SPEC §8, architecture §7):
 * `tour.offer`, `tour.start`, `tour.next`, `tour.prev`, `tour.end`. They're
 * journaled and never undoable (A-15). `tour.offer` (Help › Tour) shows the
 * offer dialog again rather than dropping the visitor into step 1.
 *
 * The commands only move the tour. A director (`driveTour`) reacts to each new
 * step by issuing that step's commands with origin `tour`, so tour commands
 * never run other commands from inside the bus. It snapshots the selection at
 * start and restores it, transiently, at the end, so a tour leaves the undo
 * stack exactly as it found it.
 */
import type { CommandBus } from "../../commands/CommandBus";
import type { CommandDefinition } from "../../commands/types";
import type { ChangeScheduler } from "../../core/ChangeScheduler";
import type { Unsubscribe } from "../../core/Emitter";
import { VersionedStore } from "../../core/store";
import type { ReadableStore } from "../../core/store";
import type { WorldReader } from "../../world/World";
import type { SelectionState } from "../selection/Selection";
import { TOUR_STEPS } from "./steps";

export interface TourState {
  /** The offer dialog is up again (Help › Tour); the first-run offer is the app's. */
  readonly offered: boolean;
  readonly active: boolean;
  /** Index into TOUR_STEPS. */
  readonly step: number;
  /** The last tour reached its end (Finish), rather than being skipped. */
  readonly completed: boolean;
  /** The selection when the tour started, restored when it ends. */
  readonly savedSelection: SelectionState | null;
}

const IDLE: TourState = {
  offered: false,
  active: false,
  step: 0,
  completed: false,
  savedSelection: null,
};

export class TourService implements ReadableStore<TourState> {
  private readonly store: VersionedStore<TourState>;

  constructor(scheduler: ChangeScheduler) {
    this.store = new VersionedStore(scheduler, IDLE);
  }

  readonly subscribe = (listener: () => void) => this.store.subscribe(listener);
  readonly getSnapshot = (): TourState => this.store.getSnapshot();

  /** @internal For the tour commands only. */
  publish(state: TourState): void {
    this.store.publish(state);
  }
}

export function tourCommands(
  tour: TourService,
  selection: ReadableStore<SelectionState>,
): CommandDefinition[] {
  const command = (
    type: string,
    describe: string,
    change: (state: TourState) => TourState | string,
  ): CommandDefinition<Record<string, never>, undefined> => ({
    type,
    undoable: false,
    // `change` is pure, so validation can try it without side effects; the
    // journaled payload stays `{}` (the tour's state is not part of the request).
    validate: () => {
      const result = change(tour.getSnapshot());
      return typeof result === "string" ? { ok: false, error: result } : { ok: true, value: {} };
    },
    do: () => {
      const result = change(tour.getSnapshot());
      if (typeof result !== "string") tour.publish(result);
      return undefined;
    },
    undo: () => undefined,
    describe: () => describe,
  });

  const last = TOUR_STEPS.length - 1;
  return [
    command("tour.offer", "Offer tour", (state) =>
      state.active ? "A tour is running" : { ...state, offered: true },
    ),
    command("tour.start", "Start tour", () => ({
      offered: false,
      active: true,
      step: 0,
      completed: false,
      savedSelection: selection.getSnapshot(),
    })),
    command("tour.next", "Tour: next step", (state) => {
      if (!state.active) return "No tour is running";
      return state.step >= last
        ? { ...state, active: false, completed: true }
        : { ...state, step: state.step + 1 };
    }),
    command("tour.prev", "Tour: previous step", (state) => {
      if (!state.active) return "No tour is running";
      if (state.step === 0) return "Already at the first step";
      return { ...state, step: state.step - 1 };
    }),
    // Ends a running tour, or dismisses the offer.
    command("tour.end", "End tour", (state) => {
      if (state.active) return { ...state, active: false };
      return state.offered ? { ...state, offered: false } : "No tour is running";
    }),
  ] as CommandDefinition[];
}

export interface TourDirectorOptions {
  readonly tour: ReadableStore<TourState>;
  readonly bus: Pick<CommandBus, "execute">;
  readonly world: WorldReader;
}

/** Issues each entered step's commands, and restores the selection when the tour ends. */
export function driveTour({ tour, bus, world }: TourDirectorOptions): Unsubscribe {
  let previous = tour.getSnapshot();

  return tour.subscribe(() => {
    const state = tour.getSnapshot();
    const entered = state.active && (!previous.active || state.step !== previous.step);
    const ended = previous.active && !state.active;
    previous = state;

    if (entered) {
      for (const request of TOUR_STEPS[state.step]?.enter(world.getSnapshot()) ?? []) {
        bus.execute({ ...request, origin: "tour" });
      }
    }
    if (ended && state.savedSelection) {
      const { ids, active } = state.savedSelection;
      bus.execute({
        type: "selection.set",
        payload: { ids, ...(active !== null && { active }), transient: true },
        origin: "tour",
      });
    }
  });
}
